import { useEffect, useState } from 'react'
import {
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import { Language as LanguageIcon } from '@mui/icons-material'

interface WISConsentClausesProps {
  tenantMaintenance: boolean
  gardenMaintenanceGroundFloor: boolean
  postHandoverDefects24h: boolean
  tenantImprovements: boolean
  masterKeyConsent: boolean
  onChange: (field: string, value: boolean) => void
  disabled?: boolean
  showError?: boolean
  defaultLanguage?: 'pl' | 'en'
}

const WISConsentClauses = ({
  tenantMaintenance,
  gardenMaintenanceGroundFloor,
  postHandoverDefects24h,
  tenantImprovements,
  masterKeyConsent,
  onChange,
  disabled = false,
  showError = false,
  defaultLanguage = 'pl'
}: WISConsentClausesProps) => {
  const [language, setLanguage] = useState<'pl' | 'en'>(defaultLanguage)

  // Follow the client's language when the selected client changes
  useEffect(() => {
    setLanguage(defaultLanguage)
  }, [defaultLanguage])

  const clauses = [
    {
      field: 'tenantMaintenance',
      value: tenantMaintenance,
      labelPl:
        'Klient odpowiada za prawidłowe utrzymanie Przedmiotu Zakwaterowania pod względem technicznym oraz za utrzymanie go w czystości. Do drobnych nakładów, do których realizacji na swój koszt i ryzyko jest zobowiązany Klient, należą drobne naprawy podłóg, drzwi i okien, żaluzji i rolet, malowanie ścian, podłóg oraz wewnętrznej strony drzwi wejściowych, jak również drobne naprawy instalacji i urządzeń technicznych, zapewniających korzystanie ze światła (w tym wymiana źródeł światła), dopływu i odpływu wody.',
      labelEn:
        'The Client is responsible for the proper technical maintenance of the Subject of the Accommodation and for keeping it clean. Minor expenses that the Client is obliged to cover at their own cost and risk include small repairs of floors, doors, and windows, blinds and shutters, painting of walls, floors, and the interior side of entrance doors, as well as minor repairs of installations and technical devices ensuring the use of light (including the replacement of light sources) and the inflow and outflow of water.'
    },
    {
      field: 'gardenMaintenanceGroundFloor',
      value: gardenMaintenanceGroundFloor,
      labelPl:
        'Klient korzystający z Przedmiotu Zakwaterowania znajdującego się na parterze z dostępem do ogródka zobowiązany jest do dbania o zieleń, trawę oraz krzewy w tym podlewanie, a także udostępnienie w uzgodnionym z R4R terminie ogródka celem wykonania w nim niezbędnych prac konserwacyjnych i pielęgnacyjnych.',
      labelEn:
        'The Client using the Subject of the Accommodation located on the ground floor with access to a garden is obliged to take care of the greenery, grass, and shrubs, including watering, as well as to provide access to the garden at a time agreed upon with R4R for the purpose of performing necessary maintenance and care work.'
    },
    {
      field: 'postHandoverDefects24h',
      value: postHandoverDefects24h,
      labelPl:
        'Do godziny 23:59 w dniu następującym po dniu podpisania protokołu, Klient dodatkowo uprawniony jest do zgłoszenia za pośrednictwem Strefy Klienta wad, które nie zostały wykryte podczas wydania Przedmiotu Zakwaterowania. Strony uzgadniają, że niezgłoszenie przez Klienta dodatkowych uwag w ww. terminie będzie oznaczało, że Przedmiot Zakwaterowania został wydany z chwilą podpisania protokołu, według stanu w nim opisanego.',
      labelEn:
        "Until 11:59 PM on the day following the day of signing the protocol, the Client is additionally entitled to report, via the Client's Centre, any defects that were not detected during the handover of the Subject of the Accommodation. The Parties agree that failure by the Client to submit additional remarks within the aforementioned time frame will mean that the Subject of the Accommodation was handed over at the time of signing the protocol in the condition described therein."
    },
    {
      field: 'tenantImprovements',
      value: tenantImprovements,
      labelPl:
        'W przypadku dokonania przez Klienta nakładów lub ulepszeń, Klient może według swego wyboru albo zdecydować o pozostawieniu nakładów lub ulepszeń w Przedmiocie Zakwaterowania bez prawa do żądania zapłaty, albo przywrócić Przedmiot Zakwaterowania do stanu poprzedniego. Pozostawienie nakładów lub ulepszeń w Przedmiocie Zakwaterowania wymaga uprzedniej pisemnej zgody R4R.',
      labelEn:
        'In the event that the Client makes expenditures or improvements, the Client may, at their discretion, either decide to leave such expenditures or improvements in the Subject of the Accommodation without the right to demand payment or restore the Subject of the Accommodation to its previous condition. Leaving expenditures or improvements in the Subject of the Accommodation requires prior written consent from R4R.'
    },
    {
      field: 'masterKeyConsent',
      value: masterKeyConsent,
      labelPl:
        'Niniejszym oświadczam, że zostałem poinformowany, że R4R dysponuje tzw. kluczem master oraz wyrażam zgodę na użycie klucza master i wejście do Lokalu osób upoważnionych przez R4R w następujących przypadkach: ' +
        '• w przypadku podejrzenia wystąpienia zagrożenia dla mienia, zdrowia lub życia osób przebywających w Lokalu lub w Obiekcie, w którym znajduje się Lokal; ' +
        '• po 2-krotnym bezskutecznym a uprzednio umówionym wejściu do Lokalu celem wykonania czynności serwisowych lub konserwacyjnych wynikających z obowiązujących przepisów prawa lub zobowiązań umownych R4R. ' +
        'Jednocześnie przyjmuję do wiadomości, że klucz master może zostać użyty jedynie w ww. przypadkach i nie będę żądał od R4R udostępnienia mi go w innym celu, np. w sytuacji zagubienia przekazanego mi kompletu kluczy do Lokalu.',
      labelEn:
        'I declare that I have been informed that R4R possesses a so-called master key and I consent to the use of the master key and the entry into the Premises by individuals authorized by R4R in the following cases: ' +
        '• in the event of suspected danger to property, health, or the lives of individuals present in the Premises or in the Facility where the Premises are located; ' +
        "• after two unsuccessful but previously scheduled attempts to enter the Premises for the purpose of performing maintenance or servicing activities required by applicable legal regulations or R4R's contractual obligations. " +
        'At the same time, I acknowledge that the master key may only be used in the aforementioned cases, and I undertake not to request R4R to provide it to me for any other purpose, e.g., in the case of losing the set of keys to the Premises that was provided to me.'
    }
  ]

  const allClausesAccepted = clauses.every((clause) => clause.value)

  const handleAcceptAll = (checked: boolean) => {
    clauses.forEach((clause) => {
      onChange(clause.field, checked)
    })
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1
        }}
      >
        <Typography variant='h6'>
          {language === 'pl' ? 'Zgody mieszkańca' : 'Tenant Consents'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguageIcon color='action' fontSize='small' />
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={(_, newLanguage) => {
              if (newLanguage) setLanguage(newLanguage)
            }}
            size='small'
          >
            <ToggleButton value='pl'>PL</ToggleButton>
            <ToggleButton value='en'>EN</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      {showError && !allClausesAccepted && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {language === 'pl'
            ? 'Wszystkie klauzule muszą być zaakceptowane przed podpisaniem protokołu'
            : 'All clauses must be accepted before signing the protocol'}
        </Alert>
      )}
      <FormControl component='fieldset'>
        <FormGroup>
          {clauses.map((clause, index) => (
            <FormControlLabel
              key={clause.field}
              control={
                <Checkbox
                  checked={clause.value}
                  onChange={(e) => onChange(clause.field, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box>
                  <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {language === 'pl' ? `Klauzula ${index + 1}` : `Clause ${index + 1}`}
                  </Typography>
                  <Typography variant='body2' sx={{ textAlign: 'justify' }}>
                    {language === 'pl' ? clause.labelPl : clause.labelEn}
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: 'flex-start' }}
            />
          ))}
          <FormControlLabel
            control={
              <Checkbox
                checked={allClausesAccepted}
                onChange={(e) => handleAcceptAll(e.target.checked)}
                disabled={disabled}
              />
            }
            label={
              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                {language === 'pl'
                  ? 'Potwierdzam, że zapoznałem się ze wszystkimi klauzulami'
                  : 'I confirm that I have read all clauses'}
              </Typography>
            }
            sx={{ mt: 2, backgroundColor: '#f5f5f5', p: 1.5, borderRadius: 1 }}
          />
        </FormGroup>
      </FormControl>
    </Box>
  )
}

export default WISConsentClauses
